import type { Deployment, Host, Service, ToolFunction, SystemdUnit, ListeningPort, ProcessInfo } from '../types'

export interface ProbeResult {
  name: string
  label?: string
  group?: string
  url: string
  http: number
  ms: number
  status: string
  ok: boolean
  kind?: 'up' | 'gated' | 'down'
  error?: string
  location?: string | null
}

export interface HealthPayload {
  now: number
  vantage: string
  healthy: number
  total: number
  up?: number
  gated?: number
  down?: number
  attention?: ProbeResult[]
  results: ProbeResult[]
}

export interface LogEntry {
  time: string
  ts: number
  level: 'success' | 'warning' | 'error' | 'info'
  message: string
  source: string
}

export interface HostInventory {
  hosts: Record<string, Host>
  external: Record<string, Service>
  deployments: Record<string, Deployment>
}

export function isPublicUrl(url: string): boolean {
  try {
    const u = new URL(url)
    return /(^|\.)(demiggwp\.me|pages\.dev|stremio\.rocks)$/i.test(u.hostname)
  } catch {
    return false
  }
}

export async function fetchHealth(): Promise<HealthPayload> {
  const res = await fetch('/api/health', { cache: 'no-store' })
  if (!res.ok) throw new Error(`health ${res.status}`)
  return res.json()
}

export async function probeUrl(url: string): Promise<ProbeResult> {
  const res = await fetch(`/api/probe?url=${encodeURIComponent(url)}`, { cache: 'no-store' })
  const body = await res.json()
  if (!res.ok && !body.status) throw new Error(body.error || `probe ${res.status}`)
  return body
}

export async function fetchHostInventory(): Promise<HostInventory> {
  const paths = ['/api/inventory', '/api/hosts']
  let lastErr: Error | null = null
  for (const path of paths) {
    try {
      const res = await fetch(path, { cache: 'no-store' })
      if (!res.ok) {
        lastErr = new Error(`${path} ${res.status}`)
        continue
      }
      return res.json()
    } catch (err) {
      lastErr = err instanceof Error ? err : new Error(String(err))
    }
  }
  throw lastErr || new Error('inventory unavailable')
}

export async function fetchServiceTools(serviceUrl: string): Promise<ToolFunction[]> {
  const res = await fetch(`/api/tools?url=${encodeURIComponent(serviceUrl)}`, { cache: 'no-store' })
  if (!res.ok) return []
  return res.json()
}

export function applyProbes(
  hosts: Record<string, Host>,
  external: Record<string, Service>,
  deployments: Record<string, Deployment>,
  probes: ProbeResult[],
) {
  const byUrl = new Map(probes.map((p) => [p.url.replace(/\/$/, ''), p]))
  const lookup = (url: string) => byUrl.get(url.replace(/\/$/, ''))

  const nextHosts: Record<string, Host> = {}
  for (const [key, host] of Object.entries(hosts)) {
    nextHosts[key] = {
      ...host,
      last_check: Date.now(),
      services: host.services.map((svc) => {
        const hit = lookup(svc.url)
        if (!hit) {
          return {
            ...svc,
            status: isPublicUrl(svc.url) ? svc.status : 'lan-unprobed',
            last_check: Date.now(),
            metadata: { ...svc.metadata, vantage: isPublicUrl(svc.url) ? 'unmatched' : 'lan-only' },
          }
        }
        return {
          ...svc,
          status: hit.status,
          last_check: Date.now(),
          metadata: { ...svc.metadata, http: hit.http, ms: hit.ms, vantage: 'cloudflare-edge' },
        }
      }),
    }
  }

  const nextExternal: Record<string, Service> = {}
  for (const [key, svc] of Object.entries(external)) {
    const hit = lookup(svc.url) || probes.find((p) => p.name === key)
    nextExternal[key] = hit
      ? { ...svc, status: hit.status, last_check: Date.now(), metadata: { ...svc.metadata, http: hit.http, ms: hit.ms, vantage: 'cloudflare-edge' } }
      : { ...svc, status: isPublicUrl(svc.url) ? svc.status : 'lan-unprobed', last_check: Date.now(), metadata: { ...svc.metadata, vantage: isPublicUrl(svc.url) ? 'unmatched' : 'lan-only' } }
  }

  const nextDeployments: Record<string, Deployment> = {}
  for (const [key, dep] of Object.entries(deployments)) {
    const hit = lookup(dep.url) || probes.find((p) => p.name === key || p.name === `${key}.demiggwp.me`)
    nextDeployments[key] = hit
      ? { ...dep, status: hit.ok ? 'deployed' : 'failed' }
      : dep
  }

  return { hosts: nextHosts, external: nextExternal, deployments: nextDeployments }
}

export function logsFromProbes(probes: ProbeResult[]): LogEntry[] {
  return probes.map((p) => ({
    time: new Date().toISOString().replace('T', ' ').slice(0, 19),
    ts: Date.now(),
    level: p.ok ? 'success' : p.status === 'access-gated' ? 'warning' : 'error',
    message: `${p.label || p.name}: ${p.status} HTTP ${p.http} ${p.ms}ms`,
    source: 'edge-probe',
  }))
}

export function kindOf(p: ProbeResult): 'up' | 'gated' | 'down' {
  if (p.kind) return p.kind
  if (p.ok) return 'up'
  if (p.status === 'access-gated') return 'gated'
  return 'down'
}

export function isForgeJobName(name: string): boolean {
  return /^forge-job-\d+$/.test(name)
}

export async function deleteForgeJob(projectId: string): Promise<{ success: boolean; errors: string[]; raw: unknown }> {
  const res = await fetch('/api/delete', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ projectId, confirm: true }),
  })
  const raw = await res.json().catch(() => ({ error: `delete ${res.status}` }))
  const errors = Array.isArray((raw as { errors?: string[] }).errors)
    ? (raw as { errors: string[] }).errors
    : (raw as { error?: string }).error
      ? [(raw as { error: string }).error]
      : res.ok
        ? []
        : [`delete ${res.status}`]
  return { success: res.ok && errors.length === 0, errors, raw }
}